import { copyFor } from "./copy";
import { useEffect, useState } from "react";
import {
  browserFlowUrl,
  createFlow,
  getFlow,
  submitFlow,
  type Flow,
  type FlowType,
  type KratosErrorBody,
  type Outcome,
  type SuccessBody,
  type UiNode,
  type UiText,
} from "./kratos";
import { href, leave, navigate } from "./router";

export const nodeNamed = (flow: Flow, name: string) => flow.ui.nodes.find(n => n.attributes.name === name);
export const nodesIn = (flow: Flow, group: string) => flow.ui.nodes.filter(n => n.group === group);
export const hasGroup = (flow: Flow, group: string) => flow.ui.nodes.some(n => n.group === group);
export const valueOf = (flow: Flow, name: string) => {
  const value = nodeNamed(flow, name)?.attributes.value;
  return typeof value === "string" ? value : "";
};
export const csrfToken = (flow: Flow) => valueOf(flow, "csrf_token");
export const loginChallenge = (flow: Flow) => flow.oauth2_login_request?.challenge ?? flow.oauth2_login_challenge;
export const errorsFor = (flow: Flow, name: string): UiText[] => nodeNamed(flow, name)?.messages ?? [];
/** Errors on fields the page doesn't show, such as the username the API derives from the email. */
export const hiddenErrors = (flow: Flow, shown: string[]): UiText[] =>
  flow.ui.nodes.filter(n => !shown.includes(n.attributes.name)).flatMap(n =>
    n.messages.filter(m => m.type === "error").map(m => ({ ...m, text: copyFor(n.attributes.name, m) })),
  );
export const submitNodes = (flow: Flow, group: string): UiNode[] =>
  nodesIn(flow, group).filter(n => n.attributes.type === "submit");

/** Go wherever Kratos or Hydra pointed: inside the portal without a reload, anywhere else with one. */
export function go(to: string) {
  const url = new URL(to, window.location.origin);
  if (url.origin === window.location.origin) navigate(url.pathname + url.search);
  else leave(url.toString());
}

const homeOr = (returnTo?: string | null) => returnTo || "/";

// Signed in already, an OAuth sign-in has nothing to show here: Kratos accepts it with Hydra and redirects a browser
// there, while a fetch only gets an empty answer. So the browser goes to Kratos.
const continueOAuth = (challenge: string) => leave(browserFlowUrl("login", { login_challenge: challenge }));

export interface FlowState {
  flow?: Flow;
  /** Set when the flow can't be shown; the page renders it as the screen's error. */
  failure?: string;
  pending: boolean;
  submit: (body: Record<string, unknown>, onSuccess: (body: SuccessBody) => void) => Promise<void>;
  setFlow: (flow: Flow) => void;
}

/**
 * Loads the flow named in ?flow=, or starts one, and keeps the address bar on ?flow=<id> so a reload resumes it.
 * Expired or unknown flows restart from the same page.
 */
export function useFlow(type: FlowType, path: string, query: URLSearchParams, start: Record<string, string | undefined>): FlowState {
  const flowId = query.get("flow");
  const [flow, setFlow] = useState<Flow>();
  const [failure, setFailure] = useState<string>();
  const [pending, setPending] = useState(false);
  const restart = () => navigate(href(path, { return_to: query.get("return_to") }), { replace: true });
  // Keeps the page's own parameters (a prefilled email, say) when the address bar moves to ?flow=<id>.
  const withFlow = (id: string) => {
    const next = new URLSearchParams(query);
    next.set("flow", id);
    return `${path}?${next}`;
  };

  const handleError = (status: number, body: KratosErrorBody, loading: boolean) => {
    const id = body.error?.id;
    if (id === "session_already_available") {
      const challenge = query.get("login_challenge");
      return challenge ? continueOAuth(challenge) : go(homeOr(query.get("return_to")));
    }
    if (id === "session_aal2_required") {
      return navigate(href("/login", { aal: "aal2", return_to: window.location.href }), { replace: true });
    }
    if (id === "session_inactive" || status === 401) {
      return navigate(href("/login", { return_to: window.location.href }), { replace: true });
    }
    if (id === "security_csrf_violation" || (loading && (status === 403 || status === 404))) return restart();
    setFailure(body.error?.reason || body.error?.message || "Something went wrong. Try again.");
  };

  useEffect(() => {
    let alive = true;
    setFailure(undefined);
    (async () => {
      let outcome: Outcome;
      try {
        outcome = flowId ? await getFlow(type, flowId) : await createFlow(type, start);
      } catch {
        if (alive) setFailure("The account service can’t be reached. Check your connection and try again.");
        return;
      }
      if (!alive) return;
      switch (outcome.kind) {
        case "flow":
          setFlow(outcome.flow);
          if (outcome.flow.id !== flowId) navigate(withFlow(outcome.flow.id), { replace: true });
          return;
        case "redirect":
          return go(outcome.to);
        case "expired":
          return outcome.flowId ? navigate(withFlow(outcome.flowId), { replace: true }) : restart();
        case "success": {
          const challenge = query.get("login_challenge");
          return challenge ? continueOAuth(challenge) : go(homeOr(query.get("return_to")));
        }
        case "error":
          return handleError(outcome.status, outcome.body, true);
      }
    })();
    return () => {
      alive = false;
    };
    // The flow id is the identity of the screen; the start parameters only matter when there is none.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, flowId]);

  async function submit(body: Record<string, unknown>, onSuccess: (body: SuccessBody) => void) {
    if (!flow || pending) return;
    setPending(true);
    try {
      const outcome = await submitFlow(flow, { csrf_token: csrfToken(flow), ...body });
      switch (outcome.kind) {
        case "flow":
          setFlow(outcome.flow);
          return;
        case "redirect":
          return go(outcome.to);
        case "success":
          return onSuccess(outcome.body);
        case "expired":
          return outcome.flowId ? navigate(withFlow(outcome.flowId), { replace: true }) : restart();
        case "error":
          return handleError(outcome.status, outcome.body, false);
      }
    } catch {
      setFailure("The account service can’t be reached. Check your connection and try again.");
    } finally {
      setPending(false);
    }
  }

  return { flow, failure, pending, submit, setFlow };
}
